// @ts-nocheck
import React from 'react';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, BarChart, Bar, XAxis, YAxis, CartesianGrid } from 'recharts';
import { Departamento } from '../../types';

interface DepartamentoGraficosProps {
  departamento: Departamento;
  onStatusClick?: (sliceName: string) => void;
}

const DepartamentoGraficos: React.FC<DepartamentoGraficosProps> = ({ departamento, onStatusClick }) => {
  // Calcular dados para os gráficos (Number() evita concatenação de strings)
  const tarefasPendentes = Number(departamento.tarefas?.pendentes ?? departamento.tarefas_pendentes ?? 0);
  const tarefasAIniciar = Number(departamento.tarefas?.aIniciar ?? 0);
  const tarefasEmAndamento = Number(departamento.tarefas?.emAndamento ?? departamento.tarefas_em_andamento ?? 0);
  const tarefasConcluidas = Number(departamento.tarefas?.concluidas ?? departamento.tarefas_concluidas ?? 0);
  const tarefasArquivadas = Number(departamento.tarefas?.arquivadas ?? 0);
  const tarefasConcluidasComAtraso = Number(departamento.tarefas?.concluidasComAtraso ?? 0);
  const tarefasConcluidasNoPrazo = Math.max(0, tarefasConcluidas - tarefasConcluidasComAtraso);
  const tarefasEmAndamentoComAtraso = Number(departamento.tarefas?.emAndamentoComAtraso ?? departamento.tarefas_em_andamento_com_atraso ?? 0);
  // No prazo vem calculado do backend (inclui atrasadas arquivadas no "com atraso");
  // fallback por subtração para compatibilidade com backend antigo
  const tarefasEmAndamentoNoPrazo = departamento.tarefas?.emAndamentoNoPrazo != null
    ? Number(departamento.tarefas.emAndamentoNoPrazo)
    : Math.max(0, tarefasEmAndamento - tarefasEmAndamentoComAtraso);

  const totalTarefas = tarefasPendentes + tarefasAIniciar + tarefasEmAndamento +
                       tarefasConcluidas + tarefasArquivadas;

  // Para taxa de conclusão: arquivadas não entram no denominador
  const totalParaTaxa = tarefasPendentes + tarefasAIniciar + tarefasEmAndamento + tarefasConcluidas;

  const dadosPizza = [
    { name: 'Pendentes', value: tarefasPendentes, color: '#EAB308' },
    { name: 'A iniciar', value: tarefasAIniciar, color: '#3B82F6' },
    { name: 'Em Andamento', value: tarefasEmAndamentoNoPrazo, color: '#A855F7' },
    { name: 'Em And. c/ Atraso', value: tarefasEmAndamentoComAtraso, color: '#F97316' },
    { name: 'Concluídas no Prazo', value: tarefasConcluidasNoPrazo, color: '#22C55E' },
    { name: 'Concluídas c/ Atraso', value: tarefasConcluidasComAtraso, color: '#EF4444' },
    { name: 'Arquivadas', value: tarefasArquivadas, color: '#6B7280' },
  ].filter(item => item.value > 0);

  // Dados para o gráfico de barras (não usado mais, mas mantido para compatibilidade)
  const dadosBarras = [
    { name: 'Pendentes', valor: tarefasPendentes, fill: '#EAB308' },
    { name: 'A iniciar', valor: tarefasAIniciar, fill: '#3B82F6' },
    { name: 'Em Andamento', valor: tarefasEmAndamento, fill: '#A855F7' },
    { name: 'Concluídas', valor: tarefasConcluidas, fill: '#22C55E' },
    { name: 'Arquivadas', valor: tarefasArquivadas, fill: '#6B7280' },
  ];

  // Mostra a porcentagem real com 1 decimal, evitando que valores pequenos
  // porém reais (ex: 3 de 1280 = 0,2%) apareçam arredondados para "0%"
  const formatarPercentual = (valor: number, total: number): string => {
    if (total <= 0) return '0';
    const pct = (valor / total) * 100;
    return pct.toFixed(1);
  };

  const CustomTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white p-3 border border-gray-200 rounded shadow-lg">
          <p className="font-medium text-gray-800">{payload[0].name}</p>
          <p className="text-sm text-gray-600">
            {payload[0].value} tarefas ({((payload[0].value / totalTarefas) * 100).toFixed(1)}%)
          </p>
        </div>
      );
    }
    return null;
  };

  return (
    <div className="bg-white rounded-lg shadow-md p-6">
      <div className="flex justify-between items-start mb-6">
        <h3 className="text-lg font-semibold text-gray-800">{departamento.nome}</h3>
        <span className="bg-blue-100 text-blue-800 text-xs font-medium px-2.5 py-0.5 rounded">
          {totalTarefas} tarefas
        </span>
      </div>

      {totalTarefas > 0 ? (
        <div className="flex flex-col items-center">
          {/* Gráfico de Pizza — clique na fatia abre as tarefas do status */}
          <div
            className="w-full max-w-md"
            onClick={(e) => { if (onStatusClick) e.stopPropagation(); }}
          >
            <h4 className="text-sm font-medium text-gray-700 mb-3 text-center">Distribuição por Status</h4>
            <ResponsiveContainer width="100%" height={280}>
              <PieChart>
                <Pie
                  data={dadosPizza}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={false}
                  outerRadius={100}
                  fill="#8884d8"
                  dataKey="value"
                  style={onStatusClick ? { cursor: 'pointer' } : undefined}
                  onClick={(data: any) => {
                    if (onStatusClick && data?.name) onStatusClick(data.name);
                  }}
                >
                  {dadosPizza.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip content={<CustomTooltip />} />
              </PieChart>
            </ResponsiveContainer>
          </div>

          {/* Legenda abaixo do gráfico — clique abre as tarefas do status */}
          <div
            className="w-full max-w-md mt-4"
            onClick={(e) => { if (onStatusClick) e.stopPropagation(); }}
          >
            <div className="grid grid-cols-2 gap-3">
              {dadosPizza.map((item, index) => (
                <div
                  key={index}
                  className={`flex items-center rounded-md p-1 -m-1 ${onStatusClick ? 'cursor-pointer hover:bg-gray-100' : ''}`}
                  onClick={() => { if (onStatusClick) onStatusClick(item.name); }}
                >
                  <div
                    className="w-4 h-4 rounded-sm mr-2 flex-shrink-0"
                    style={{ backgroundColor: item.color }}
                  />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-gray-700 truncate">{item.name}</p>
                    <p className="text-xs font-semibold" style={{ color: item.color }}>
                      {item.value} ({formatarPercentual(item.value, totalTarefas)}%)
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      ) : (
        <div className="text-center py-8 text-gray-500">
          <svg xmlns="http://www.w3.org/2000/svg" className="h-16 w-16 mx-auto mb-3 text-gray-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
          </svg>
          <p className="text-sm">Nenhuma tarefa registrada neste departamento</p>
        </div>
      )}

      {/* Estatísticas rápidas */}
      <div className="mt-6 pt-4 border-t border-gray-200">
        <div className="grid grid-cols-2 gap-4 text-center">
          <div>
            <p className="text-xs text-gray-500">Ativas</p>
            <p className="text-lg font-bold text-blue-600">
              {departamento.tarefas
                ? (departamento.tarefas.pendentes || 0) + (departamento.tarefas.aIniciar || 0) + (departamento.tarefas.emAndamento || 0)
                : Number(departamento.tarefas_ativas ?? 0)}
            </p>
          </div>
          <div
            className={onStatusClick ? 'cursor-pointer rounded-md hover:bg-gray-100' : ''}
            onClick={(e) => { if (onStatusClick) { e.stopPropagation(); onStatusClick('Concluídas'); } }}
          >
            <p className="text-xs text-gray-500">Concluídas</p>
            <p className="text-lg font-bold text-green-600">{tarefasConcluidas}</p>
          </div>
          <div
            className={onStatusClick ? 'cursor-pointer rounded-md hover:bg-gray-100' : ''}
            onClick={(e) => { if (onStatusClick) { e.stopPropagation(); onStatusClick('Concluídas c/ Atraso'); } }}
          >
            <p className="text-xs text-gray-500">Concl. c/ Atraso</p>
            <p className="text-lg font-bold text-red-600">
              {Number(departamento.tarefas?.concluidasComAtraso ?? 0)}
            </p>
          </div>
          <div
            className={onStatusClick ? 'cursor-pointer rounded-md hover:bg-gray-100' : ''}
            onClick={(e) => { if (onStatusClick) { e.stopPropagation(); onStatusClick('Em And. c/ Atraso'); } }}
          >
            <p className="text-xs text-gray-500">Em And. c/ Atraso</p>
            <p className="text-lg font-bold text-orange-500">
              {tarefasEmAndamentoComAtraso}
            </p>
          </div>
          <div className="col-span-2">
            <p className="text-xs text-gray-500">Taxa de Conclusão</p>
            <p className="text-lg font-bold text-purple-600">
              {totalParaTaxa > 0 ? ((tarefasConcluidas / totalParaTaxa) * 100).toFixed(0) : 0}%
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DepartamentoGraficos;
